import CartVoucher from 'src/entities/cart/response/CartVoucher';

/**
 * Live-Runde 10: what the shop keeps between two calcs — the server echoes neither the codes the
 * customer unchecked nor the product picked for a product group voucher.
 */
export interface VoucherChoice {
    declined: string[];
    targets: Record<string, number>;
}

/** The catalogue vouchers a request carries: checked, unchecked and the picked products. */
export interface VoucherSelection extends VoucherChoice {
    codes: string[];
}

export const voucherChoiceFrom = (json: any): VoucherChoice => ({
    declined: Array.isArray(json?.declined) ? json.declined.filter((code: unknown) => typeof code === 'string') : [],
    targets: json?.targets && typeof json.targets === 'object' ? { ...json.targets } : {},
});

/**
 * The selection as the last calc left it: a code the customer checked stays checked, the Rabatt-Gutschein
 * the server preselected is left to the server (it proposes the highest valid one anew), and choices
 * for vouchers the customer no longer has are dropped.
 */
export const currentSelection = (vouchers: CartVoucher[], choice: VoucherChoice): VoucherSelection => {
    const known = new Set(vouchers.map(voucher => voucher.code).filter((code): code is string => !!code));
    return {
        codes: vouchers
            .filter(voucher => voucher.code && voucher.selected && !voucher.preselected)
            .map(voucher => voucher.code as string),
        declined: choice.declined.filter(code => known.has(code)),
        targets: Object.fromEntries(Object.entries(choice.targets).filter(([code]) => known.has(code))),
    };
};

export const checkVoucher = (selection: VoucherSelection, code: string, target?: number): VoucherSelection => ({
    codes: selection.codes.includes(code) ? selection.codes : [...selection.codes, code],
    declined: selection.declined.filter(declined => declined !== code),
    targets: target != null ? { ...selection.targets, [code]: target } : selection.targets,
});

/** Unchecked (checkbox or X): the voucher stays listed, a Rabatt-Gutschein is not proposed again. */
export const uncheckVoucher = (selection: VoucherSelection, code: string): VoucherSelection => ({
    codes: selection.codes.filter(checked => checked !== code),
    declined: selection.declined.includes(code) ? selection.declined : [...selection.declined, code],
    targets: selection.targets,
});

/** The product line a free piece of this voucher folds into. */
export const freePieceTarget = (voucher: CartVoucher, choice: VoucherChoice): number | undefined => {
    if (voucher.kind === 'PRODUKT') return voucher.productNumber;
    if (voucher.kind !== 'PRODUKTGRUPPE' || !voucher.code) return undefined;
    return choice.targets[voucher.code] ?? voucher.targetProductNumber;
};

/** Deleting a product deletes its free pieces too — else the next calc would bring the product back. */
export const withoutFreePiecesOf = (
    selection: VoucherSelection,
    vouchers: CartVoucher[],
    productNumber: string | number
): VoucherSelection =>
    vouchers
        .filter(
            voucher =>
                voucher.code &&
                selection.codes.includes(voucher.code) &&
                String(freePieceTarget(voucher, selection)) === String(productNumber)
        )
        .reduce((next, voucher) => uncheckVoucher(next, voucher.code as string), selection);

export const isVoucherListed = (voucher: CartVoucher, choice: VoucherChoice): boolean =>
    voucher.selected || (voucher.code != null && choice.declined.includes(voucher.code));
